import {
  ExchangeAccountsDocument,
  ExchangeAccountDocument,
  ExchangeAccountsPaginatedDocument,
  ExchangeCreateDocument,
  ExchangeUpdateDocument,
  ExchangeDeleteDocument,
  ExchangeActivateDocument,
  ExchangeDeactivateDocument,
  AssetsPaginatedDocument,
  ExchangeVerifyCredentialsDocument,
  ExchangeSyncHistoryDocument,
} from "api/generated";
import { RequestTypes } from "api/services/enums";
import { getRequestInstance } from "api/services/utils";
import { Optional } from "config";
import { executeEviction } from "api/utils/cache-eviction";
import { client } from "config/apollo";

import {
  IAccountExchangeModel,
  IExchangeAccountModel,
  IExchangeSyncLogModel,
  IGetExchangeAccountsPaginatedArgs,
  IGetExchangeAccountsPaginatedResponse,
  ICreateExchangeArgs,
  IUpdateExchangeArgs,
  IDeleteExchangeArgs,
  IGetExchangeListArgs,
  IGetExchangeListResponse,
  IVerifyExchangeCredentialsArgs,
  IVerifyExchangeCredentialsResponse,
} from "./types";

export async function getExchangeAccounts(): Promise<IAccountExchangeModel[]> {
  const makeRequest = getRequestInstance(RequestTypes.QUERY);
  const res = await makeRequest({
    query: ExchangeAccountsDocument,
    fetchPolicy: "network-only",
  });

  return res?.data?.exchangeAccounts ?? [];
}

export async function getExchangeAccount(
  exchangeId: number
): Promise<Optional<IExchangeAccountModel>> {
  const makeRequest = getRequestInstance(RequestTypes.QUERY);
  const res = await makeRequest({
    query: ExchangeAccountDocument,
    variables: { exchangeId },
    fetchPolicy: "network-only",
  });

  return res?.data?.exchangeAccount;
}

export async function getExchangeAccountsPaginated(
  variables?: IGetExchangeAccountsPaginatedArgs
): Promise<IGetExchangeAccountsPaginatedResponse> {
  const makeRequest = getRequestInstance(RequestTypes.QUERY);
  const res = await makeRequest({
    query: ExchangeAccountsPaginatedDocument,
    variables,
    fetchPolicy: "network-only",
  });

  return res?.data?.exchangeAccountsPaginated;
}

export async function createExchangeAccount(
  variables: ICreateExchangeArgs
): Promise<Optional<IAccountExchangeModel>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: ExchangeCreateDocument,
      variables,
      fetchPolicy: "no-cache",
    });
    executeEviction(client, {
      typename: "ExchangeListModelPaginatedNode",
      fieldNames: [
        "exchangeAccountsPaginated",
        "assetsPaginated",
        "financeDashboardAssetPositionsPaginated",
      ],
    });

    return res?.data?.exchangeCreate;
  } catch (error) {
    throw error;
  }
}

export async function updateExchangeAccount(
  variables: IUpdateExchangeArgs
): Promise<Optional<IAccountExchangeModel>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: ExchangeUpdateDocument,
      variables,
      fetchPolicy: "no-cache",
    });
    executeEviction(client, {
      typename: "ExchangeListModelPaginatedNode",
      fieldNames: [
        "exchangeAccountsPaginated",
        "exchangeAccount",
        "assetsPaginated",
        "financeDashboardAssetPositionsPaginated",
      ],
    });

    return res?.data?.exchangeUpdate;
  } catch (error) {
    throw error;
  }
}

export async function deleteExchangeAccount(
  variables: IDeleteExchangeArgs
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: ExchangeDeleteDocument,
      variables,
      fetchPolicy: "no-cache",
    });
    executeEviction(client, {
      typename: "ExchangeListModelPaginatedNode",
      fieldNames: [
        "exchangeAccountsPaginated",
        "assetsPaginated",
        "financeDashboardAssetPositionsPaginated",
      ],
    });

    return Boolean(res?.data?.exchangeDelete);
  } catch (error) {
    throw error;
  }
}

export async function activateExchangeAccount(
  variables: IDeleteExchangeArgs
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: ExchangeActivateDocument,
      variables,
      fetchPolicy: "no-cache",
    });
    executeEviction(client, {
      typename: "ExchangeListModelPaginatedNode",
      fieldNames: [
        "exchangeAccountsPaginated",
        "exchangeAccount",
        "assetsPaginated",
        "financeDashboardAssetPositionsPaginated",
      ],
    });

    return Boolean(res?.data?.exchangeActivate);
  } catch (error) {
    throw error;
  }
}

export async function deactivateExchangeAccount(
  variables: IDeleteExchangeArgs
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: ExchangeDeactivateDocument,
      variables,
      fetchPolicy: "no-cache",
    });
    executeEviction(client, {
      typename: "ExchangeListModelPaginatedNode",
      fieldNames: [
        "exchangeAccountsPaginated",
        "exchangeAccount",
        "assetsPaginated",
        "financeDashboardAssetPositionsPaginated",
      ],
    });

    return Boolean(res?.data?.exchangeDeactivate);
  } catch (error) {
    throw error;
  }
}

export async function getExchangeList(
  variables?: IGetExchangeListArgs
): Promise<IGetExchangeListResponse> {
  const makeRequest = getRequestInstance(RequestTypes.QUERY);
  const res = await makeRequest({
    query: AssetsPaginatedDocument,
    variables,
    fetchPolicy: "network-only",
  });

  return res?.data?.assetsPaginated;
}

export async function getExchangeSyncHistory(
  exchangeId: number,
  limit?: number
): Promise<IExchangeSyncLogModel[]> {
  const makeRequest = getRequestInstance(RequestTypes.QUERY);
  const res = await makeRequest({
    query: ExchangeSyncHistoryDocument,
    variables: { exchangeId, limit },
    fetchPolicy: "network-only",
  });

  return res?.data?.exchangeSyncHistory ?? [];
}

export async function verifyExchangeAccountCredentials(
  variables: IVerifyExchangeCredentialsArgs
): Promise<Optional<IVerifyExchangeCredentialsResponse>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: ExchangeVerifyCredentialsDocument,
      variables,
      fetchPolicy: "no-cache",
      context: { skipErrorToast: true },
    });

    return res?.data?.exchangeVerifyCredentials;
  } catch (error) {
    console.error("Exchange credential verification request failed");
    throw error;
  }
}
